import React from 'react';
import { Shield, ShieldOff, Trash2 } from 'lucide-react';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import type { UserProfile } from '../../../types/user';

interface UserListProps {
  users: UserProfile[];
  isLoading: boolean;
  onUpdate: (userId: string, updates: Partial<UserProfile>) => Promise<void>;
  onDelete: (userId: string) => Promise<void>;
  onRefresh: () => void;
}

export function UserList({ users, isLoading, onUpdate, onDelete, onRefresh }: UserListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No users found.</p>
      </div>
    );
  }

  const handleToggleAdmin = async (userId: string, isCurrentlyAdmin: boolean) => {
    await onUpdate(userId, { is_admin: !isCurrentlyAdmin });
    onRefresh();
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      await onDelete(userId);
      onRefresh();
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Career Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <img
                        className="h-10 w-10 rounded-full"
                        src={user.avatar_url || `https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}`}
                        alt={`${user.first_name} ${user.last_name}`}
                      />
                    </div>
                    <div className="ml-4">
                      <div className="flex flex-col text-xs text-gray-500">
                        <span>Last login: {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}</span>
                        <span>IP: {user.last_ip_address || 'Unknown'}</span>
                        <span>Login count: {user.login_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.is_admin
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    {user.is_admin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span className="capitalize">{user.career_level || 'Not Set'}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.location || 'Not Set'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                      className="p-2 text-gray-400 hover:text-gray-500 rounded-lg hover:bg-gray-100"
                      title={user.is_admin ? 'Remove admin rights' : 'Make admin'}
                    >
                      {user.is_admin ? (
                        <ShieldOff className="h-5 w-5" />
                      ) : (
                        <Shield className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100"
                      title="Delete user"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}